<template>
  <v-card 
    color="white"
    dark
  >
    <!-- Print Confirmation Dialog. Printing is a heavy side-effect for a mis-click. -->
    <v-dialog 
      v-model="showPrintConfirmation"
      max-width="600px"
    >
      <v-card>
        <v-card-title class="pa-0 pb-2">
          <v-toolbar color="grey lighten-2 elevation-0">
            <v-btn
              icon
              class="mr-2"
              @click="showPrintConfirmation = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <span>Download & Print Floor Plan?</span>
          </v-toolbar>
        </v-card-title>
        <v-card-text>
          <div>Download exactly as displayed in PDF Format.</div>
          <div>Go to your computer downloads to print.</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="printLoading"
            @click="showPrintConfirmation = false"
          >
            Cancel
          </v-btn>
          <v-btn
            text
            color="primary"
            :loading="printLoading"
            @click="doPrint"
          >
            Download & Print
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-row 
      no-gutters
      class="d-flex justify-space-between"
    >
      <v-col 
        cols="12"
        sm="6"
        md="4"
      >
        <div class="d-flex align-self-center">
          <v-tooltip
            bottom
            open-delay="400"
          >
            <template #activator="{ on, attrs }">
              <v-hover v-slot="{ hover }">
                <v-btn
                  :color="hover ? 'primary' : 'grey darken-1'"
                  outlined
                  max-width="100%"
                  class="mx-1 d-block"
                  v-bind="attrs"
                  v-on="getOn('locationBtnContent', on)"
                  @click="$emit('find-meal-period', false)"
                >
                  <span
                    ref="locationBtnContent"
                    class="text-truncate"
                    :style="{'max-width': '100%'}"
                  >
                    {{ location.name || 'Select Location' }}
                  </span>
                </v-btn>
              </v-hover>
            </template>
            {{ location.name || 'Select Location' }}
          </v-tooltip>
        </div>
      </v-col>
      <v-col 
        cols="12"
        sm="6"
        md="4"
        align-self="center"
      >
        <div class="d-flex justify-center">
          <v-hover v-slot="{ hover }">
            <v-btn
              :color="hover && !forEvent ? 'primary' : `grey ${forEvent ? 'lighten-1' : 'darken-1'}`"
              outlined
              class="mx-1"
              :ripple="!forEvent"
              :style="{cursor: forEvent ? 'default' : 'pointer'}"
              @click="forEvent ? () => {} : $emit('find-meal-period', true)"
            >
              {{ formattedDate || 'Select Date' }}
            </v-btn>
          </v-hover>
        </div>
      </v-col>
      <v-col 
        cols="12"
        sm="6"
        md="4"
        align-self="center"
        class="pr-1"
      >
        <div class="d-flex justify-end">
          <v-menu 
            offset-y
            transition="slide-y-transition"
          >
            <template #activator="{ on, attrs }">
              <v-tooltip 
                bottom
                open-delay="400"
              >
                <template #activator="data">
                  <v-hover v-slot="{ hover }">
                    <v-btn
                      :color="(hover || attrs['aria-expanded'] === 'true') && !forEvent ? 'primary' : `grey ${forEvent ? 'lighten' : 'darken'}-1`"
                      outlined
                      max-width="100%"
                      :class="['d-block', {'pl-1': !forEvent}]"
                      :disabled="!(location.name && date)"
                      v-bind="{...attrs, ...data.attrs}"
                      :ripple="!forEvent"
                      :style="{transition: 'color 0.15s ease-in-out', cursor: forEvent ? 'default' : 'pointer'}"
                      v-on="forEvent ? {} : {...on, ...getOn('mealPeriodBtnContent', data.on)}"
                    > 
                      <rotator 
                        v-if="!forEvent"
                        :rotated="attrs['aria-expanded'] === 'true'"
                        state-two="-180deg"
                      >
                        <v-icon class="pl-0">
                          mdi-chevron-down
                        </v-icon>
                      </rotator>
                      <span
                        ref="mealPeriodBtnContent"
                        class="text-truncate"
                        :style="{'max-width': '100%'}"
                      >
                        <span v-if="forEvent">
                          {{ event.name || 'Select Event' }}
                        </span>
                        <span v-else>
                          {{ mealPeriod.name || 'Select Meal Period' }}
                        </span>
                      </span>
                    </v-btn>
                  </v-hover>
                </template>
                {{ `${forEvent ? event.name : mealPeriod.name}` || 'Select Meal Period' }}
              </v-tooltip>
            </template>
            <v-list class="pa-0">
              <v-list-item
                v-for="mealPeriodItem in mealPeriods || []"
                :key="'floor-plan-header-meal-period-' + mealPeriodItem.id"
                @click="$emit('set-meal-period', mealPeriodItem)"
              >
                <v-list-item-content>
                  {{ mealPeriodItem.name || 'UNDEF' }}
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col 
        cols="12"
        sm="6"
        md="4"
        class="pl-1"
      >
        <div v-if="$vuetify.breakpoint.xs">
          <v-icon @click="$emit('toggle-time-radar')">
            mdi-radar
          </v-icon>
        </div>
        <div 
          v-else
          class="d-flex align-self-center"
        >
          <v-hover v-slot="{ hover }">
            <v-btn
              :color="hover || timeRadarExpanded ? 'primary' : 'grey darken-1'"
              outlined
              @click="$emit('toggle-time-radar')"
            >
              <rotator 
                state-two="-180"
                :rotated="timeRadarExpanded"
              >
                <v-icon>
                  mdi-chevron-down
                </v-icon>
              </rotator>
              Reservation Radar
            </v-btn>
          </v-hover>
        </div>
      </v-col>
      <v-col 
        cols="12"
        sm="6"
        md="4"
        align-self="center"
      >
        <div class="d-flex justify-center">
          <v-menu 
            offset-y
            transition="slide-y-transition"
          >
            <template #activator="{ on, attrs }">
              <v-tooltip bottom>
                <template #activator="tooltip">
                  <v-hover v-slot="{ hover }">
                    <v-btn
                      :color="hover ? 'primary' : 'grey darken-1'"
                      outlined
                      elevation="0"
                      max-width="100%"
                      class="mx-1 pl-1 d-block"
                      v-bind="attrs"
                      v-on="Object.assign(on, getOn('tableConfig', tooltip.on))"
                    >
                      <rotator
                        :rotated="attrs['aria-expanded'] === 'true'"
                        state-two="-180deg"
                      >
                        <v-icon>mdi-chevron-down</v-icon>
                      </rotator>
                      <span
                        ref="tableConfig"
                        class="text-truncate"
                        :style="{'max-width': '100%'}"
                      >
                        {{ tableConfiguration.name || 'UNDEF' }}
                      </span>
                    </v-btn>
                  </v-hover>
                </template>
                {{ tableConfiguration.name || 'Select Table Configuration' }}
              </v-tooltip>
            </template>
            <v-list>
              <div 
                v-for="(configuration, index) of tableConfigurations"
                :key="'table-configuration-' + configuration.id"
              >
                <v-list-item @click="$emit('set-table-config', index)">
                  <v-list-item-content>{{ configuration.name }}</v-list-item-content>
                </v-list-item>
                <v-divider 
                  v-if="index !== tableConfigurations.length - 1"
                  :key="'table-configuration-divider-' + index" 
                />
              </div>
            </v-list>
          </v-menu>
        </div>
      </v-col>
      <v-col 
        cols="12"
        sm="6"
        md="4"
        align-self="center"
      >
        <div class="d-flex justify-end">
          <v-hover v-slot="{ hover }">
            <v-btn
              :color="hover ? 'primary' : 'grey darken-1'"
              icon
              class="mx-1"
              @click="showPrintConfirmation = true"
            >
              <v-icon>mdi-printer</v-icon>
            </v-btn>
          </v-hover>
          <!-- <v-btn
            icon
            :style="{transition: 'all 0.2s ease-in-out'}"
            :class="{'primary darken-1 elevation-3': multiSelectMode}"
            class="mx-1"
            @click="$emit('enable-multi-select')"
          >
            <v-icon>mdi-vector-selection</v-icon>
          </v-btn> -->
          <v-hover v-slot="{ hover }">
            <v-btn
              :color="hover ? 'primary' : 'grey darken-1'"
              icon
              class="mx-1"
              @click="$emit('refresh')"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-hover>
        </div>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import floorPlanHelpers from './mixins/floorPlanHelpers'
import Rotator from '@/ft-vue-core/components/transformers/Rotator'
import moment from 'moment'
export default {
  name: 'FloorPlanHeader',
  components: {Rotator},
  mixins: [floorPlanHelpers],
  props: {
    location: {
      type: Object,
      default () {return {}}
    },
    mealPeriod: {
      type: Object,
      default () {return {}}
    },
    event: {
      type: Object,
      default () {return {}}
    },
    mealPeriods: Array,
    date: [String, Number],
    timeRadarExpanded: Boolean,
    tableConfiguration: {
      type: Object,
      default () {return {}}
    },
    tableConfigurations: {
      type: Array,
      defualt () {return []}
    },
    forEvent: Boolean,
    multiSelectMode: Boolean,
  },
  data () {
    return {
      showPrintConfirmation: false,
      printLoading: false,
    }
  },
  computed: {
    formattedDate () {
      return moment(this.date).format('ddd, MMM Do')
    }
  },
  methods: {
    getOn (ref, on) { // We only want to use tooltips if text IS truncated
      const {offsetWidth, scrollWidth} = this.$refs[ref] ?? {}
      if (offsetWidth === scrollWidth) return {}
      return on
    },
    doPrint () {
      this.printLoading = true
      this.$emit('print')
      setTimeout(() => {
        this.showPrintConfirmation = false
        this.printLoading = false
      }, 300)
    }
  }
}
</script>

<style scoped>
.rotate-180 {
  transform: rotate(180deg);
}
</style>